import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import ProjectsBackdrop from "@frontend/app/layout/backdrops/ProjectsBackdrop.tsx";
import LandingBackdrop from "@frontend/app/layout/backdrops/LandingBackdrop.tsx";
import ContactBackdrop from "@frontend/app/layout/backdrops/ContactBackdrop.tsx";
import CaterBackdrop from "@frontend/app/layout/backdrops/CaterBackdrop.tsx";

const ROUTES = Object.freeze([
  {
    pathName: "/projects",
    Component: ProjectsBackdrop,
  },
  {
    pathName: "/",
    Component: LandingBackdrop,
  },
  {
    pathName: "/contact",
    Component: ContactBackdrop,
  },
  {
    pathName: "/cater",
    Component: CaterBackdrop,
  },
]);

const Backdrop = () => {
  const { pathname } = useLocation();

  /**
   * Calculates the scale of the route based on the current and target route.
   * The goal is to make each backdrop appear as part of the same "universe" as the other routes.
   * For example, if you start high on the stack of routes, then navigate to the lowest route,
   * You should see the interim backdrops of the routes that are lower in the stack fly by on your way to the lowest route.
   *
   * The scale of the current route is always 1. Anything with a lower index in the stack is scaled down and any higher index is scaled up.
   * Negative scaling is not allowed, so we can't rely on the browser's anim timing to make the transitions look smooth.
   *
   * Foe example, if you have (theoretically) 3 negative routes, we can't simply set them to 0 because they'll be animated at the same time.
   * Negative scales will be provided with a delay, calculated based on the animation time allocated to each route to fly by. We'll set this to 200ms.
   * That would mean that the lowest route will fly by for 200ms, then the next route will fly by for 200ms, and so on.
   *
   * Positive scales will go up by a factor of 1, so the current will be 1, next is 2, next is 3, etc.
   * Negative scales will go down by a factor of 0.5, so 1, 0.5, 0, then the other 0 scaled negatives with their delays.
   * This means that only one of the "lower" routes will be visible in the background at a time. This background should receive a blur effect.
   *
   * For each calculation we return the scale, the delay, and the blur. Positive scales will get a blur multiplied by the scale.
   *
   * The return value is an array of objects, each with the scale, delay, and blur for a specific route.
   */
  const routesWithScale = useMemo(() => {
    const currentIndex = ROUTES.findIndex(
      route =>
        pathname === route.pathName ||
        (route.pathName !== "/" && pathname.startsWith(route.pathName))
    );
    return ROUTES.map((route, i) => {
      const delta = i - currentIndex;
      let scale: number;
      let delay = 0;
      let blur = 0;

      // If the route is below the current route in the stack
      if (delta < 0) {
        // How far below the current route in the stack
        const stepsBelow = currentIndex - i;
        scale = 0;
        // The lowest scale possible is 0, so delay for each step beyond the first
        if (scale <= 0) {
          delay = (stepsBelow - 1) * 200; // Each route delays for 200ms
        }
      } else if (delta > 0) {
        // Calculate for routes above current in the stack
        scale = 1 + delta * 3;
      } else scale = 1;

      // Calculate blur effect
      if (i !== currentIndex) {
        if (scale <= 0) {
          blur = 0;
        } else if (scale < 1) {
          blur = 1 - scale; // Blur decreases as scale approaches 1
        } else {
          blur = scale; // Blur increases with scale for higher routes
        }
      }

      return { ...route, scale, delay, blur, zIndex: (ROUTES.length - i) * 10 };
    });
  }, [pathname]);

  return (
    <div
      className="max-h-full overflow-y-hidden"
      style={{ "--shadow-bl": "17px 12px 40px 1px #1a1a1d" } as React.CSSProperties}
    >
      {routesWithScale.map(({ Component, scale, delay, blur, zIndex }, i) => (
        <Component
          key={i}
          zIndex={zIndex}
          scale={scale}
          // delay={delay}
          blur={blur}
        />
      ))}
    </div>
  );
};

export default Backdrop;
